import perf from '../utils/performance';
import breakpoints from './breakpoints';
import sizeMappings from './sizeMappings';
import targeting from './targeting';

/* Prep apntag */
window.apntag = window.apntag || {};
window.apntag.anq = window.apntag.anq || [];

let externalUid = null;

const queueReady = new Promise((resolve) => {
	window.apntag.anq.push(() => {
		resolve();
	});
});

function _hookEvents() {
	window.apntag.onEvent('adError', (error, adObj) => {
		console.error('[ADVERT] Something went wrong in an ad creative for the ad ', adObj, ' | Error: ', error);
	});
}

export async function init(settings) {
	perf.mark('xandr - apntag.init - start');

	perf.mark('xandr - apntag.cmd wait - start');
	await queueReady;
	perf.mark('xandr - apntag.cmd wait - end');

	window.apntag.clearRequest();

	window.apntag.setPageOpts(_getPageOptionsFromSettings(settings));
	setUserPageOptions();
	_hookEvents();

	perf.mark('xandr - apntag.init - end');
}

export function defineSlot(slot) {
	window.apntag.defineTag(_getTagValuesFromSlot(slot));
}

function _formatSizeMappingForXandr(sizeMap) {
	const sizeMaps = Object.entries(breakpoints.getBreakpoints()).map(([bpName, bpBegin]) => ({
		'minWidth': bpBegin,
		'sizes': (sizeMap[bpName] || []).filter((s) => s !== 'fluid')
	}));

	if (sizeMap.customBreakpointMapping) {
		return [...sizeMaps, ...sizeMap.customBreakpointMapping];
	}

	return sizeMaps;
}

export function loadSlots(slots = []) {
	window.apntag.loadTags(slots.map((s) => s.domId));
}

export function renderSlot(slot) {
	window.apntag.modifyTag(slot.domId, _getTagValuesFromSlot(slot));
	window.apntag.showTag(slot.domId);
}

export function refreshSlots(slots = []) {
	slots.forEach((slot) => {
		window.apntag.modifyTag(slot.domId, _getTagValuesFromSlot(slot));
	});

	window.apntag.refresh(slots.map((s) => s.domId));
}

function _getPageOptionsFromSettings(/** Settings */ settings) {
	externalUid = settings.user;

	return {
		'member': settings.memberId,
		'enableSafeFrame': settings.enableSafeFrame,
		'enableMediationEvents': true,
		'disablePsa': settings.disablePSA,
		'keywords': targeting.getPageTargeting()
	};
}

export function setUserPageOptions(userIds = []) {
	const userPageOption = {};

	if (externalUid) {
		userPageOption.externalUid = externalUid;
	}

	if (userIds.length > 0) {
		userPageOption.userIds = userIds;
	}

	if (Object.keys(userPageOption).length > 0) {
		window.apntag.setPageOpts({'user': userPageOption});
	}
}

export function updatePageTargeting() {
	window.apntag.setPageOpts({
		'keywords': targeting.getPageTargeting()
	});
}

function _getTagValuesFromSlot(/** Slot */ slot) {
	const placement = typeof slot.placement === 'object' ? slot.placement[breakpoints.getCurrentBreakpoint()] : slot.placement,
		placementKey = typeof placement === 'number' ? 'tagId' : 'invCode';

	return {
		'targetId': slot.domId,
		[placementKey]: placement,
		'sizeMapping': _formatSizeMappingForXandr(sizeMappings.getSizeMapping(slot.sizeMapping)),
		'keywords': targeting.getSlotTargeting(slot)
	};
}

export function resizeSlot(slot, newSize) {
	window.apntag.resizeAd(slot.domId, newSize);
}

export function addRenderFinishListener(slot, callback) {
	window.apntag.onEvent('adLoaded', slot.domId, callback);
}

export function addAvailableListeners(slot, callback) {
	window.apntag.onEvent('adAvailable', slot.domId, (adObj) => callback({
		'nobid': false,
		...adObj
	}));

	// AdObj can be missing with mediation, so we at least provide for isEmpty
	window.apntag.onEvent('adNoBid', slot.domId, (adObj) => callback({
		'nobid': true,
		...adObj ?? {}
	}));
}

export function addRequestedListeners(slot, successCallback, errorCallback) {
	window.apntag.onEvent('adRequested', slot.domId, successCallback);
	window.apntag.onEvent('adBadRequest', slot.domId, (...params) => errorCallback('adBadRequest', ...params));
	window.apntag.onEvent('adRequestFailure', slot.domId, (...params) => errorCallback('adRequestFailure', ...params));
}

export function removeRequestedListeners(slot) {
	window.apntag.offEvent('adRequested', slot.domId);
	window.apntag.offEvent('adBadRequest', slot.domId);
	window.apntag.offEvent('adRequestFailure', slot.domId);
}

export function addMediationListeners(slot, callback) {
	window.apntag.onEvent('adLoadedMediated', slot.domId, callback);
	window.apntag.onEvent('adNoBidMediated', slot.domId, (adObj) => callback({
		'nobid': true,
		...adObj ?? {}
	}));
}

export function removeRenderFinishListener(slot) {
	window.apntag.offEvent('adLoaded', slot.domId);
}

export function removeAvailableListeners(slot) {
	window.apntag.offEvent('adAvailable', slot.domId);
	window.apntag.offEvent('adNoBid', slot.domId);
}

export function removeMediationListeners(slot) {
	window.apntag.offEvent('adLoadedMediated', slot.domId);
	window.apntag.offEvent('adNoBidMediated', slot.domId);
}
